import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import * as Actions from '../../store/actions';
import * as TYPES from '../../store/actions/actions';
import { API, Auth } from 'aws-amplify';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import './style.sass';
import { Button, Col, Container, Row, Form, Modal } from 'react-bootstrap';
import { Images } from '../../components';
import { Text } from '@aws-amplify/ui-react';
import awsConfig from '../../aws-config';

const SignIn = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [validated, setValidated] = useState(false);
  const [validatedForgot, setValidatedForgot] = useState(false);
  const [validatedCode, setValidatedCode] = useState(false);
  // const [usernameForForgotPassword, setUsernameForForgotPassword] = useState("");
  const [usernameForVerification, setUsernameForVerification] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [showForgot, setShowForgot] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showVerification, setShowVerification] = useState(false);
  const [validatedVerification, setValidatedVerification] = useState(false);
  const [showForceNewPassword, setShowForceNewPassword] = useState(false);
  const [forceNewPassword, setForceNewPassword] = useState('');
  const [user, setUser] = useState({});

  const persistUserPoolTokens = ({ userId, authenticationResult }) => {
    const clientId = awsConfig?.aws_user_pools_web_client_id;
    if (!clientId) return false;
    const idToken = authenticationResult?.IdToken || authenticationResult?.idToken;
    const accessToken = authenticationResult?.AccessToken || authenticationResult?.accessToken;
    const refreshToken = authenticationResult?.RefreshToken || authenticationResult?.refreshToken;
    if (!userId || !idToken || !accessToken || !refreshToken) return false;
    const prefix = `CognitoIdentityServiceProvider.${clientId}`;
    localStorage.setItem(`${prefix}.LastAuthUser`, userId);
    localStorage.setItem(`${prefix}.${userId}.idToken`, idToken);
    localStorage.setItem(`${prefix}.${userId}.accessToken`, accessToken);
    localStorage.setItem(`${prefix}.${userId}.refreshToken`, refreshToken);
    localStorage.setItem(`${prefix}.${userId}.clockDrift`, '0');
    return true;
  };

  const redirectSignedInUser = useCallback(
    async (user) => {
      const studentId =
        user?.attributes?.username || user?.username || user?.attributes?.sub;
      const profile = user?.attributes?.profile || user?.attributes?.['custom:profile'];

      if (profile === 'STUDENT') {
        dispatch(
          Actions.dispatchMyCousesForStudent({
            studentId,
            sortDirection: 'DESC',
          }),
        )
          .then((res) => {
            if (res?.data?.listStudentPackages?.items.length > 0) {
              navigate('/my-courses');
            } else {
              navigate('/courses');
            }
          })
          .catch((err) => {
            navigate('/my-courses');
          })
          .finally(() => {
            toast.dismiss();
          });
      } else {
        toast.dismiss();
        navigate('/courses');
      }
    },
    [dispatch, navigate],
  );

  useEffect(() => {
    async function getCurrentUserInfo() {
      try {
        const currentUser = await Auth.currentUserInfo();
        if (currentUser?.attributes?.email) {
          const userAttributes = currentUser.attributes || {};
          userAttributes.username = currentUser.username || userAttributes.username;
          dispatch(Actions.submitLoginUser(userAttributes));
          toast.success('You have already signed in, redirecting...');
          setTimeout(() => {
            redirectSignedInUser({ ...currentUser, attributes: userAttributes });
          }, 0);
        }
      } catch (error) {
        console.log('error, ', error);
      } finally {
        dispatch({ type: TYPES.UI_STOP_LOADING });
      }
    }
    getCurrentUserInfo();
  }, [redirectSignedInUser, dispatch]);

  const handlePWDEnter = async (event) => {
    if (event.key === 'Enter') {
      handleSubmit();
    }
  };
  const handleSubmit = async () => {
    if (!username) {
      toast.error('Please enter username');
      return;
    } else if (!password) {
      toast.error('Please enter password');
      return;
    }
    dispatch({ type: TYPES.UI_START_LOADING });
    try {
      const res = await API.post('beecSquare', '/auth/login', {
        headers: { 'Content-Type': 'application/json' },
        body: { email: username, password },
      });
      const data = typeof res === 'string' ? JSON.parse(res) : res;
      const userId = data?.userId;
      const authenticationResult = data?.authenticationResult;
      if (!authenticationResult) {
        throw new Error('Login requires additional verification.');
      }
      const ok = persistUserPoolTokens({ userId, authenticationResult });
      if (!ok) {
        throw new Error('Login succeeded but tokens were missing.');
      }
      try {
        await Auth.currentSession();
      } catch (e) {
        await Auth.signIn(userId, password);
      }

      const currentUser = await Auth.currentUserInfo();
      if (!currentUser?.attributes?.email) {
        await Auth.currentAuthenticatedUser({ bypassCache: true });
      }
      const finalUser = await Auth.currentUserInfo();
      if (!finalUser?.attributes) {
        throw new Error('Login succeeded but user session was not initialized.');
      }
      const userAttributes = finalUser.attributes;
      userAttributes.username = userId || userAttributes.username;
      dispatch(Actions.submitLoginUser(userAttributes));
      toast.success('Successfully Signed in');
      dispatch({ type: TYPES.UI_STOP_LOADING });
      dispatch(Actions.getUserDetails({ id: userId })).then((userDetails) => {
        if (userDetails?.status === 'PENDING') {
          const input = {
            id: userId,
            status: 'ENABLED',
          };
          dispatch(Actions.dispatchUpdateUserStatus(input));
        }
      });
      redirectSignedInUser({ attributes: userAttributes });
    } catch (error) {
      dispatch({ type: TYPES.UI_STOP_LOADING });
      const message =
        error?.response?.data?.message ||
        error?.response?.body?.message ||
        error?.response?.data?.error ||
        error?.response?.body?.error ||
        error?.message ||
        'Login failed';
      toast.error(message);
    }
    setValidated(true);
  };

  const submitForgot = async (event) => {
    event.preventDefault();
    const form = event.currentTarget;
    if (form.checkValidity() === false) {
      event.stopPropagation();
    } else {
      try {
        await API.post('beecSquare', '/auth/forgot-password', {
          headers: { 'Content-Type': 'application/json' },
          body: { email: username },
        });
        toast.success('A code has been sent to ' + username);
        setShowNewPassword(true);
      } catch (error) {
        const message =
          error?.response?.data?.message ||
          error?.response?.body?.message ||
          error?.message ||
          'Failed to send verification code';
        toast.error(message);
      }
    }
    setValidatedForgot(true);
  };

  const submitCode = async (event) => {
    event.preventDefault();
    const form = event.currentTarget;
    if (form.checkValidity() === false) {
      event.stopPropagation();
    } else {
      try {
        await API.post('beecSquare', '/auth/forgot-password-submit', {
          headers: { 'Content-Type': 'application/json' },
          body: { email: username, code, newPassword: password },
        });
        toast.success('Password Reset Successfully');
        setShowNewPassword(false);
        setTimeout(() => {
          window.location.assign('/login');
        }, 1000);
      } catch (error) {
        const message =
          error?.response?.data?.message ||
          error?.response?.body?.message ||
          error?.message ||
          'Failed to reset password';
        toast.error(message);
      }
    }
    setValidatedCode(true);
  };

  const setEmptyFields = () => {
    setShowForgot(!showForgot);
    setUsername('');
    setPassword('');
    setCode('');
  };

  const submitVerification = async (event) => {
    event.preventDefault();
    const form = event.currentTarget;
    if (form.checkValidity() === false) {
      event.stopPropagation();
    } else {
      try {
        await API.post('beecSquare', '/auth/confirm-signup', {
          headers: { 'Content-Type': 'application/json' },
          body: { email: username, code },
        });
        toast.success('You have successfully registered');
        setShowVerification(false);
        navigate('/login');
      } catch (error) {
        const message =
          error?.response?.data?.message ||
          error?.response?.body?.message ||
          error?.message ||
          'Failed to verify account';
        toast.error(message);
      }
    }
    setValidated(true);
  };

  const submitForceNewPassword = async (event) => {
    event.preventDefault();
    try {
      Auth.completeNewPassword(
        user, // the Cognito User Object
        forceNewPassword, // the new password
      )
        .then((user) => {
          dispatch({ type: TYPES.UI_STOP_LOADING });
          setShowForceNewPassword(false);
          toast.success('Password Reset Successfully');
          // at this time the user is logged in if no MFA required
        })
        .catch((error) => {
          dispatch({ type: TYPES.UI_STOP_LOADING });
          toast.error(error.message);
        });
    } catch (err) {
      console.log(' submitForceNewPassword exception');
      toast.error(err.message);
    }
  };

  const resendVerificationEmail = async () => {
    try {
      const email = (usernameForVerification || username || '').trim();
      if (!email) {
        toast.error('Missing email');
        return;
      }
      await API.post('beecSquare', '/auth/resend-signup', {
        headers: { 'Content-Type': 'application/json' },
        body: { email },
      });
      toast.success('Account verification email have been sent to your email.');
    } catch (err) {
      toast.error('Failed to resend email');
      console.log('error resending code: ', err);
    }
  };

  return (
    <Container className={'signin-div'} fluid>
      <Row>
        <Col md={4} className={'first-half'}>
          <img src={Images.logoColored} />
          <Text className={'main-title'}>
            Beyond <br /> Education Consulting
          </Text>
          <Text className={'sub-title'}>Sign-in Here</Text>
          <div className={'input-group'}>
            <label className={'custom-label'}>
              Email<sup>*</sup>
            </label>
            <input
              type={'text'}
              className={'custom-input'}
              placeholder={'Email e.g smith@example.com'}
              value={username}
              onChange={(e) => setUsername(e.target.value)}
            />
          </div>
          <div className={'input-group'}>
            <label className={'custom-label'}>
              Password<sup>*</sup>
            </label>
            <input
              type={'password'}
              className={'custom-input'}
              placeholder={'***********'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyDown={handlePWDEnter}
            />
          </div>
          <Button
            className={'login-btn mt-3'}
            onClick={() => {
              handleSubmit();
            }}
          >
            Login
          </Button>

          <div className="mt-2">
            Don't have an account?{' '}
            <a className="login-text" onClick={() => navigate('/signup')}>
              Sign Up
            </a>
          </div>
          <div className="mt-2">
            <a className="login-text" onClick={() => setEmptyFields()}>
              Forgot Password?
            </a>
          </div>
          {/* <Text
            className={"text-center custom-link mt-2"}
            onClick={() => navigate("/signup")}
          >
            Don't have an account? Sign Up
          </Text>
          <Text
            className={"text-center custom-link mt-2"}
            onClick={() => setEmptyFields()}
          >
            Forgot Password?
          </Text> */}

          {/* <div className={"main"}>
            {" "}
            <ul>
              <li>Privacy Policy</li>
              <li>Terms and conditions</li>
            </ul>
          </div> */}
        </Col>
        <Col
          md={8}
          className={'second-half d-none d-md-block'}
          style={{ backgroundImage: `url(${Images.loginImage})` }}
        ></Col>
      </Row>

      <Modal
        show={showForceNewPassword}
        onHide={() => setShowForceNewPassword(false)}
        centered
        backdrop="static"
        keyboard={false}
      >
        <Modal.Header>
          <Modal.Title>New Password</Modal.Title>
        </Modal.Header>
        <Form noValidate onSubmit={submitForceNewPassword}>
          <Modal.Body>
            <Form.Group className="input-wrapper" as={Col} md="12">
              <Form.Label className="signup-labels body-2">
                New Password <span className="">*</span>
              </Form.Label>
              <Form.Control
                autoComplete="off"
                className="signup-input body-2"
                type="password"
                placeholder="Enter new password here"
                required
                value={forceNewPassword}
                onChange={(e) => setForceNewPassword(e.target.value)}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button type="submit" className="blue-button">
              Submit
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <Modal
        show={showVerification}
        onHide={() => setShowVerification(false)}
        centered
        backdrop="static"
        keyboard={false}
      >
        <Modal.Header>
          <Modal.Title>Account Verification</Modal.Title>
        </Modal.Header>
        <Form
          noValidate
          // validated={validatedVerification}
          onSubmit={submitVerification}
        >
          <p className="text-center m-3">
            We've sent an verification link to your email to activate your account. The link in the
            email expires in 24 hours.
          </p>
          <p className="text-center m-3">
            If you haven't received an emai, click on resend button.
          </p>
          {/*
          <Modal.Body>
            <Form.Group
              className="input-wrapper"
              as={Col}
              md="12"
              controlId="verification code"
            >
              <Form.Label className="signup-labels body-2">
                Confirmation Code <span className="">*</span>
              </Form.Label>
              <Form.Control
                autoComplete="off"
                className="signup-input body-2"
                type="text"
                placeholder="Enter Code Here"
                required
                value={code}
                onChange={(e) => setCode(e.target.value)}
              />
            </Form.Group>
          </Modal.Body>
          */}
          <Modal.Footer>
            <Button onClick={() => resendVerificationEmail()} className="blue-button">
              Resend
            </Button>
            <Button onClick={() => setShowVerification(false)} className="blue-button">
              Cancel
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <Modal show={showForgot} onHide={() => setShowForgot(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Forgot Password</Modal.Title>
        </Modal.Header>
        {showNewPassword ? (
          <Form noValidate validated={validatedCode} onSubmit={submitCode}>
            <Modal.Body>
              <Form.Group className="input-wrapper" as={Col} md="12" controlId="Forgot code">
                <Form.Label className="signup-labels body-2">
                  Enter Code <span className="">*</span>
                </Form.Label>
                <Form.Control
                  className="signup-input body-2"
                  type="text"
                  placeholder="Enter Code"
                  required
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                />
              </Form.Group>
              <Form.Group className="input-wrapper" as={Col} md="12" controlId="forgot password">
                <Form.Label className="signup-labels body-2">
                  Enter New Password <span className="">*</span>
                </Form.Label>
                <Form.Control
                  className="signup-input body-2"
                  type="password"
                  placeholder="Enter New Password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </Form.Group>
            </Modal.Body>
            <Modal.Footer>
              <Button type="submit" className="blue-button">
                Submit
              </Button>
            </Modal.Footer>
          </Form>
        ) : (
          <Form noValidate validated={validatedForgot} onSubmit={submitForgot}>
            <Modal.Body>
              <Form.Group className="input-wrapper" as={Col} md="12" controlId="Forgot password">
                <Form.Label className="signup-labels body-2">
                  Email Address <span className="">*</span>
                </Form.Label>
                <Form.Control
                  className="signup-input body-2"
                  type="email"
                  placeholder="Email Address"
                  required
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                />
              </Form.Group>
            </Modal.Body>
            <Modal.Footer>
              <Button type="submit" className="blue-button">
                Submit
              </Button>
            </Modal.Footer>
          </Form>
        )}
      </Modal>
    </Container>
  );
};

export default SignIn;
